// Invisible suffix that keeps otherwise identical labels apart (U+200A, hair space).
const HAIR_SPACE = String.fromCodePoint(0x200a);

const withSuffix = (base, suffix) => (suffix ? `${base} • ${suffix}` : base);

/**
 * Builds the options of a colleague picker, e.g. the Profiler feedback dialog.
 *
 * An account can span several email domains, so the same person (or two people with
 * the same name and job title) can appear twice. The label carries the email domain
 * from /get-self-company-users; when name, job title and domain still collide, those
 * entries show the full email instead. Without a domain the label stays "Name • Job".
 *
 * Labels must be unique: multiselect-react-dropdown tracks the selection by label,
 * so any remaining duplicate gets a trailing HAIR_SPACE as a last resort, as before.
 *
 * @param {Array<{ id: string, firstName?: string, lastName?: string, jobPosition?: string,
 *   email?: string, emailDomain?: string }>} users
 * @returns {Array<{ name: string, value: string }>}
 */
export const buildColleagueOptions = (users = []) => {
  const entries = users.map((u) => {
    const base = `${u.firstName} ${u.lastName} • ${u.jobPosition}`;
    return { user: u, base, name: withSuffix(base, u.emailDomain) };
  });

  const labelCounts = new Map();
  entries.forEach((e) => labelCounts.set(e.name, (labelCounts.get(e.name) || 0) + 1));

  const seen = new Set();
  return entries.map((e) => {
    const collides = labelCounts.get(e.name) > 1 && e.user.emailDomain && e.user.email;
    let name = collides ? withSuffix(e.base, e.user.email) : e.name;
    while (seen.has(name)) {
      name += HAIR_SPACE;
    }
    seen.add(name);
    return { name, value: e.user.id };
  });
};
